import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function SettingsPage() {
  const { t, can } = useApp();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = can("settings.edit");

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => setS({})); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings", { settings: s }); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  if (!s) return <div className="h-40 bg-muted rounded-md animate-pulse" />;

  const Field = ({ label, k, type = "text" }) => (
    <div><Label>{label}</Label><Input type={type} value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} disabled={!canEdit} className="mt-1.5" data-testid={`setting-${k}`} /></div>
  );

  return (
    <div>
      <PageHeader title={t("settings")} subtitle="Organization system configuration">
        {canEdit && <Button size="sm" onClick={save} disabled={saving} data-testid="settings-save-btn">{t("save")}</Button>}
      </PageHeader>
      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="settings-tab-general">{t("generalSettings")}</TabsTrigger>
          <TabsTrigger value="language" data-testid="settings-tab-language">{t("language")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">{t("security")}</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">{t("notifications")}</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <div className="bg-card border border-border rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Organization Name" k="organization_name" />
            <Field label="Contact Email" k="contact_email" />
            <Field label={t("phone")} k="phone" />
            <Field label="Timezone" k="timezone" />
            <Field label="Date Format" k="date_format" />
            <Field label="Currency" k="currency" />
            <div className="md:col-span-2"><Field label={t("address")} k="address" /></div>
          </div>
        </TabsContent>
        <TabsContent value="language">
          <div className="bg-card border border-border rounded-md p-6 space-y-4 max-w-md">
            <div><Label>Default Language</Label>
              <select value={s.default_language ?? "en"} onChange={(e) => set("default_language", e.target.value)} disabled={!canEdit}
                className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="setting-default-lang">
                <option value="en">English</option><option value="sw">Kiswahili</option>
              </select>
            </div>
            <p className="text-sm text-muted-foreground">Available languages: English, Kiswahili</p>
          </div>
        </TabsContent>
        <TabsContent value="security">
          <div className="bg-card border border-border rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            <Field label="Session Timeout (minutes)" k="session_timeout_minutes" type="number" />
            <Field label="Max Failed Logins" k="max_failed_logins" type="number" />
            <Field label="Min Password Length" k="min_password_length" type="number" />
          </div>
        </TabsContent>
        <TabsContent value="notifications">
          <div className="bg-card border border-border rounded-md p-6 max-w-md">
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium">Enable System Notifications</span>
              <Switch checked={!!s.enable_notifications} onCheckedChange={(v) => set("enable_notifications", v)} disabled={!canEdit} data-testid="setting-notifications-toggle" />
            </label>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
